import { CSSProperties, memo, useMemo } from "react";
import { List, ListItem, ListList, ListHeader, ListContent, ListDescription, Header,  } from "semantic-ui-react";

// Common
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Serializers
import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";

// FormSection
import { FormTab, parseAns, parseAnswerToQA } from "./FormSection";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

type FormViewBoxProps = {
  tab: FormTab,
  selectedForm: DiagFormSerializer | null;
  // -- pagination
  formItems: DiagFormSerializer[];
  activePage: number;
  total: number;
  // -- callback
  onSelectForm: (form: DiagFormSerializer) => void;
  onChangePage: (activePage: number) => void;
};

const STYLES: Record<string, CSSProperties> = {
  activeForm: {
    backgroundColor: "var(--primary-theme-color)",
    color: "#fff"
  }
};

const FormViewBox = (props: FormViewBoxProps) => {

  // todo -- เมื่อได้ response จริงมา ให้ไปทำที่ FormSection แล้วโยน props เข้ามา
  const formsGroupByName = useMemo(() => {
    return props.formItems.reduce<Record<string, DiagFormSerializer[]>>(
      (acc, form) => {
        const key = form.diag_rule_name || "ไม่ระบุชื่อแบบประเมิน"; // *

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(form);

        return acc;
      },
      {}
    );
  }, [props.formItems]);

  // console.log("Yeti [FormViewBox] Forms: ", props.forms);

  return (
    <div style={{ display:"flex", flexDirection: "column", gap: "1rem", padding: "1rem 0" }}>
      {props.tab === "overview" && (
        <div
          style={{ 
            height: "50vh",
            overflowY: "auto",
            border: "solid 1px rgba(1, 1, 1, 0.2)",
            borderRadius: "8px",
            padding: "1rem"
          }}
        >
          <List divided relaxed>
            {Object.entries(formsGroupByName).map(([groupName, forms])=> (
              <ListItem key={groupName}>
                <Header
                  as="h3"
                  style={{
                    marginTop: "8px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  {groupName}
                </Header>

                <ListList>
                  {forms.map((form, idx) => (
                    <ListItem key={form.id}>
                      <div>
                        <span>{idx + 1}. </span>
                        <label>วันที่ทำแบบประเมิน : {formatISOtoBEDateTime(form.updated).dateTimeShort}</label>
                      </div>

                      <div style={{ margin: "0.5rem 0 0.5rem 1rem" }}>
                        <label><strong>ผลการประเมิน</strong> : {parseAns(form.answers)}</label>
                      </div>
                    </ListItem>
                  ))}
                </ListList>
              </ListItem>
            ))}
          </List>
        </div>
      )}
        

      {props.tab === "history" && (
        <div
          style={{ 
            height: "50vh",
            border: "solid 1px rgba(1, 1, 1, 0.2)",
            borderRadius: "8px",
            padding: "1rem",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            {/* -- Left Section -- */}
            <div
              style={{
                width: "35%",
                paddingRight: "1rem",
                borderRight: "solid 1px rgba(1, 1, 1, 0.2)",
                overflowY: "auto"
              }}
            >
              <List selection divided relaxed>
                {props.formItems.map((form) => (
                  <ListItem
                    key={form.id}
                    active={props.selectedForm?.id === form.id}
                    onClick={() => props.onSelectForm(form)}
                    style={{
                      ...(props.selectedForm?.id === form.id ? STYLES.activeForm : {}),
                      minHeight: "5.5rem",
                      // display: "flex",
                      // alignItems: "center",
                    }}
                  >
                    <ListContent>
                      <ListHeader as="h4" style={{ marginBottom: "0.5rem" }}>{form.diag_rule_name}</ListHeader>
                      <ListDescription style={{ fontSize: 11 }}>
                        {formatISOtoBEDateTime(form.updated).dateTimeShort}
                      </ListDescription>
                    </ListContent>
                  </ListItem>
                ))}
              </List>
            </div>

            {/* --- Right Section --- */}
            <div style={{ flex: 1, paddingLeft: "1rem", overflowY: "auto" }}>
              {!props.selectedForm && (
                <div style={{ opacity: 0.6, display: "flex", justifyContent: "center", alignItems: "center", height: "45vh" }}>
                  กรุณาเลือกแบบประเมินจากรายการด้านซ้าย
                </div>
              )}

              {props.selectedForm && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                    <Header as="h3" style={{ margin: 0 }}>สรุปผลการประเมิน:</Header>{" "}
                    <label>{parseAns(props.selectedForm.answers)}</label>
                  </div>

                  <Header as="h3">รายละเอียดการตอบแบบประเมิน</Header>

                  <List relaxed>
                    {parseAnswerToQA(props.selectedForm.answers).map((qa, idx) => (
                      <ListItem key={idx}>
                        <div>
                          <label><strong>Question</strong> : {qa.question}</label>
                        </div>

                        <div style={{ margin: "0.5rem 0 0.5rem 1rem" }}>
                          <label><strong>Answer</strong> : {qa.answer}</label>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <PaginationShorthand
          activePage={props.activePage || 1}
          limit={10}
          total={props.total}
          onPageChange={(activePage) => props.onChangePage(activePage)}
        />
      </div>
    </div>
  );
};

FormViewBox.displayName = "FormViewBox";

export default memo(FormViewBox);